// src/components/EmailPromptModal.tsx
// Email binding prompt — extracted from AuthProvider for modularity

import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store'
import { callAuth } from '@/lib/authClient'
import { Button } from './ui/Button'

const EMAIL_PROMPT_DISMISS_KEY = 'email_bind_prompt_dismissed_until'
const EMAIL_PROMPT_DISMISS_DAYS = 1

export function EmailPromptModal() {
  const location = useLocation()
  const { user, isAuthenticated, login } = useAuthStore()

  const [emailValue, setEmailValue] = useState('')
  const [emailSaving, setEmailSaving] = useState(false)
  const [emailPromptError, setEmailPromptError] = useState<string | null>(null)
  const [emailPromptDismissed, setEmailPromptDismissed] = useState(false)
  const [emailVerificationSent, setEmailVerificationSent] = useState(false)
  const [emailResending, setEmailResending] = useState(false)
  const [isEditingPendingEmail, setIsEditingPendingEmail] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      setEmailPromptDismissed(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!user?.email) {
      setEmailValue('')
      setIsEditingPendingEmail(false)
      return
    }
    if (!isEditingPendingEmail) {
      setEmailValue(user.email)
    }
  }, [user?.email, isEditingPendingEmail])

  const isEmailLinked = Boolean(
    user?.email && user?.email_verified_at
  )

  const isEmailPending = Boolean(user?.email && !user?.email_verified_at)

  const isDismissedTemporarily = (() => {
    if (emailPromptDismissed) return true
    const raw = localStorage.getItem(EMAIL_PROMPT_DISMISS_KEY)
    if (!raw) return false
    const until = Number(raw)
    return Number.isFinite(until) && Date.now() < until
  })()

  const normalizedPromptEmail = emailValue.trim().toLowerCase()
  const isPromptEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedPromptEmail)

  const shouldShow = Boolean(
    isAuthenticated && user && !isEmailLinked && !isDismissedTemporarily && location.pathname !== '/email-verified'
  )

  const dismiss = () => {
    const until = Date.now() + EMAIL_PROMPT_DISMISS_DAYS * 24 * 60 * 60 * 1000
    localStorage.setItem(EMAIL_PROMPT_DISMISS_KEY, String(until))
    setEmailPromptDismissed(true)
    setEmailPromptError(null)
  }

  const saveEmail = async () => {
    if (!normalizedPromptEmail || !isPromptEmailValid) {
      setEmailPromptError(!normalizedPromptEmail ? 'Введите email' : 'Проверьте формат email')
      return
    }

    setEmailSaving(true)
    setEmailPromptError(null)

    try {
      await callAuth('link-email', { email: normalizedPromptEmail })

      if (user) {
        login({
          ...user,
          email: normalizedPromptEmail,
          email_verified_at: null,
          auth_method: user.auth_method,
          updated_at: new Date().toISOString()
        })
      }

      localStorage.removeItem(EMAIL_PROMPT_DISMISS_KEY)
      setEmailPromptDismissed(false)
      setEmailPromptError(null)
      setEmailVerificationSent(true)
      setIsEditingPendingEmail(false)
    } catch (error) {
      setEmailVerificationSent(false)
      setEmailPromptError(error instanceof Error ? error.message : 'Не удалось сохранить email')
    } finally {
      setEmailSaving(false)
      setEmailResending(false)
    }
  }

  if (!shouldShow) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-md rounded-xl bg-card p-5 shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-md px-2 py-1 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Закрыть окно"
        >
          x
        </button>
        <h3 className="mb-2 text-lg font-semibold text-gray-900">
          {isEmailPending ? 'Подтвердите email' : 'Привяжите email для входа'}
        </h3>

        {isEmailPending && !emailVerificationSent && !isEditingPendingEmail ? (
          <>
            <p className="mb-1 text-sm text-gray-600">
              Email <span className="font-medium">{user?.email}</span> сохранен, но не подтвержден.
            </p>
            <p className="mb-3 text-xs text-gray-500">Проверьте почту или отправьте ссылку повторно.</p>
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <span className="text-amber-600 text-sm">Статус: Не подтвержден</span>
            </div>
            {emailPromptError && <p className="mb-3 text-sm text-red-600">{emailPromptError}</p>}
            <div className="space-y-2">
              <Button size="sm" className="w-full" onClick={() => void saveEmail()} disabled={emailSaving || emailResending}>
                {emailResending ? 'Отправляем...' : 'Отправить ссылку снова'}
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => {
                  setEmailVerificationSent(false); setEmailPromptError(null); setIsEditingPendingEmail(true); setEmailValue(user?.email ?? '')
                }}>
                  Изменить email
                </Button>
                <Button variant="outline" size="sm" className="flex-1" onClick={dismiss}>Напомнить позже</Button>
              </div>
            </div>
          </>
        ) : emailVerificationSent ? (
          <>
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <span className="text-emerald-700 text-sm">
                Ссылка для подтверждения отправлена на <span className="font-medium">{normalizedPromptEmail || user?.email}</span>.
                Откройте письмо и перейдите по ссылке.
              </span>
            </div>
            {emailPromptError && <p className="mb-3 text-sm text-red-600">{emailPromptError}</p>}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => void saveEmail()} disabled={emailSaving || emailResending}>
                {emailResending ? 'Отправляем...' : 'Отправить ссылку снова'}
              </Button>
              <Button variant="outline" size="sm" className="flex-1" onClick={() => {
                setEmailVerificationSent(false); setEmailPromptError(null); setIsEditingPendingEmail(true); setEmailValue(user?.email ?? '')
              }}>
                Изменить email
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="mb-1 text-sm text-gray-600">Это резервный способ входа, если Telegram временно недоступен.</p>
            <p className="mb-4 text-xs text-gray-500">На указанный email будет отправлена ссылка для подтверждения.</p>
            <input
              type="email"
              value={emailValue}
              onChange={(e) => { setEmailValue(e.target.value); setEmailPromptError(null); setEmailVerificationSent(false) }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !emailSaving && isPromptEmailValid) void saveEmail() }}
              placeholder="you@gmail.com"
              className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              disabled={emailSaving}
            />
            {emailValue.length > 0 && !isPromptEmailValid && <p className="mb-3 text-xs text-red-600">Проверьте формат email</p>}
            {emailPromptError && <p className="mb-3 text-sm text-red-600">{emailPromptError}</p>}
            <div className="flex items-center justify-end gap-2">
              {isEditingPendingEmail ? (
                <Button variant="outline" size="sm" onClick={() => {
                  setIsEditingPendingEmail(false); setEmailValue(user?.email ?? ''); setEmailPromptError(null)
                }} disabled={emailSaving}>Отмена</Button>
              ) : (
                <Button variant="outline" size="sm" onClick={dismiss} disabled={emailSaving}>Напомнить позже</Button>
              )}
              <Button size="sm" onClick={() => void saveEmail()} disabled={emailSaving || !isPromptEmailValid}>
                {emailSaving ? 'Сохраняем...' : 'Сохранить и отправить ссылку'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
